import { useState } from "react";
import useSWR from "swr";

import { Icon } from "../../components/Icon";
import {
  AdAvatar,
  Callout,
  Drawer,
  Empty,
  Field,
  Pill,
  Section,
  ToggleRow,
  errText,
  fmtDateTime,
  useToast,
} from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminUsersPage,
  type Me,
  type ReportRecipientGate,
} from "../../services/api";

/**
 * Monthly Client Service Review — the per-customer distribution list and the
 * product toggle for the reporting engine.
 *
 * Two gates decide whether a customer receives anything on the 7th, and both
 * live here: `monthly_review_enabled` (off by default, so a customer with no
 * row receives nothing) and at least one recipient (the dispatcher skips a
 * company with an empty list). Neither has any other UI, so this section is
 * the only way to configure a send.
 *
 * Recipients are deliberately NOT portal users. An external address with no
 * account is a first-class case, which is why this is a customer-level list
 * rather than a per-person setting in Users & access — though the drawer
 * offers this customer's own people first, since that is the common case.
 */

const GATE: Record<ReportRecipientGate, { kind: "ok" | "warn" | "off" | "danger"; label: string }> =
  {
    eligible: { kind: "ok", label: "Will receive" },
    monthly_review_disabled: { kind: "off", label: "Reviews off" },
    blocked_suppressed: { kind: "danger", label: "Suppressed" },
    blocked_domain: { kind: "warn", label: "Domain not allowed" },
  };

/** Default the test send to the month a real send would cover. */
function lastMonth(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function ReportingSection({
  client,
  me,
}: {
  client: AdminClientDetail;
  me: Me;
}) {
  const toast = useToast();
  const { data, mutate, error } = useSWR(
    ["/api/admin/clients", client.id, "report-recipients"],
    () => api.admin.clients.reporting.recipients(client.id),
  );

  const [busy, setBusy] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const reviewOn = data?.monthly_review_enabled ?? false;
  const recipients = data?.recipients ?? [];

  async function toggleReview(next: boolean) {
    setBusy(true);
    setListError(null);
    try {
      await api.admin.clients.reporting.setConfig(client.id, next);
      await mutate();
      toast(next ? "Monthly review enabled" : "Monthly review disabled", next ? "ok" : "warn");
    } catch (e) {
      setListError(errText(e, "Could not change the monthly review setting."));
    } finally {
      setBusy(false);
    }
  }

  async function remove(email: string) {
    if (!window.confirm(`Stop sending ${client.name}'s review to ${email}?`)) return;
    setBusy(true);
    setListError(null);
    try {
      await api.admin.clients.reporting.removeRecipient(client.id, email);
      await mutate();
      toast("Recipient removed");
    } catch (e) {
      setListError(errText(e, "Could not remove that recipient."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Section
        title="Monthly Client Service Review"
        desc="On the 7th of each month, and on no other day, these people are told last month's review is ready. The email carries no figures and no attachment — the link opens the report in the portal, behind its own access checks."
        error={listError ?? (error ? String((error as Error).message ?? error) : null)}
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy}
            onClick={() => setAdding(true)}
          >
            <Icon name="userPlus" size={15} /> Add recipient
          </button>
        }
      >
        <ToggleRow
          label="Send the monthly review to this customer"
          sub={
            reviewOn
              ? "On the 7th, everyone below who is eligible receives it."
              : "Off. Nobody at this customer receives a review, whatever the list below says."
          }
          checked={reviewOn}
          disabled={busy}
          onChange={(v) => void toggleReview(v)}
        />

        {reviewOn && recipients.length === 0 && (
          <div style={{ marginTop: 14 }}>
            <Callout kind="warn">
              Reviews are on but the list is empty, so this customer is skipped on the 7th.
              Add whoever should receive it.
            </Callout>
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          {recipients.length === 0 ? (
            <Empty
              title="No recipients yet"
              blurb="Add the people who should receive this customer's review."
            />
          ) : (
            <div className="ad-tablewrap">
              <table className="ad-table">
                <thead>
                  <tr>
                    <th>Recipient</th>
                    <th>Source</th>
                    <th>Added</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r) => {
                    const gate = GATE[r.gate_status];
                    return (
                      <tr key={r.id}>
                        <td>
                          <span className="who">
                            <AdAvatar name={r.display_name || r.email} />
                            <span>
                              <span className="n">{r.display_name ?? r.email}</span>
                              {r.display_name && (
                                <>
                                  <br />
                                  <span className="e">{r.email}</span>
                                </>
                              )}
                            </span>
                          </span>
                        </td>
                        <td className="nowrap">
                          {r.source === "portal_user" ? "Portal user" : "External"}
                        </td>
                        <td className="mono nowrap">{fmtDateTime(r.added_at)}</td>
                        <td className="nowrap">
                          <Pill kind={gate.kind}>{gate.label}</Pill>
                        </td>
                        <td className="r">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={busy}
                            onClick={() => void remove(r.email)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Section>

      {me.role === "iquest_admin" && <TestSend client={client} me={me} />}

      {adding && (
        <AddRecipientDrawer
          client={client}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false);
            await mutate();
          }}
        />
      )}
    </>
  );
}

/**
 * Send the letter to the signed-in staff member's own inbox.
 *
 * Customers receive the review on the 7th and on no other day, so this is the
 * only way to look at it in between. There is no destination field by design:
 * it can never be aimed at a client, and it does not consume that month's real
 * send.
 */
function TestSend({ client, me }: { client: AdminClientDetail; me: Me }) {
  const [period, setPeriod] = useState(lastMonth());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.admin.clients.reporting.sendTestEmail(client.id, period);
      setResult(`Review for ${res.period} sent to ${res.sent_to}.`);
    } catch (e) {
      setError(errText(e, "Could not send the test."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Send yourself a test"
      desc={`Sends ${client.name}'s review letter to your own inbox (${me.email}) so you can check it on any day. It never reaches the recipients above, and it doesn't use up their send for that month.`}
      error={error}
    >
      <div className="ad-grid">
        <Field label="Report month">
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </Field>
        <div className="ad-field" style={{ alignSelf: "end" }}>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy || !period}
            onClick={() => void send()}
          >
            <Icon name="send" size={15} /> {busy ? "Sending…" : "Send test to me"}
          </button>
        </div>
      </div>
      {result && (
        <div className="form-success" style={{ marginTop: 12 }}>
          {result}
        </div>
      )}
    </Section>
  );
}

function AddRecipientDrawer({
  client,
  onClose,
  onAdded,
}: {
  client: AdminClientDetail;
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const toast = useToast();
  const { data: users } = useSWR<AdminUsersPage>(
    ["/api/admin/users", client.id, "report-recipients"],
    () => api.admin.users.list({ company_id: client.id, page: 1, page_size: 200 }),
  );

  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeUsers = (users?.items ?? []).filter((u) => u.status === "active");
  const usingUser = Boolean(userId);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const chosen = activeUsers.find((u) => u.id === userId);
      const payload = usingUser
        ? {
            email: chosen?.email ?? "",
            display_name: chosen?.display_name ?? null,
            portal_user_id: userId,
          }
        : { email: email.trim(), display_name: name.trim() || null };
      if (!payload.email) {
        setError("Pick one of their people, or type an external address.");
        setBusy(false);
        return;
      }
      await api.admin.clients.reporting.addRecipient(client.id, payload);
      await onAdded();
      toast(`${payload.email} added`);
    } catch (e) {
      setError(errText(e, "Could not add that recipient."));
      setBusy(false);
    }
  }

  return (
    <Drawer
      eyebrow={client.name}
      title="Add a review recipient"
      desc="They receive the monthly review letter by email. A portal account is not required."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? "Adding…" : "Add recipient"}
          </button>
        </>
      }
    >
      <div className="ad-eyebrow" style={{ marginBottom: 8 }}>
        One of their people
      </div>
      {activeUsers.length === 0 ? (
        <div className="ad-note" style={{ marginBottom: 16 }}>
          This customer has no active portal users yet.
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {activeUsers.map((u) => (
            <button
              key={u.id}
              type="button"
              className={`ad-pick${userId === u.id ? " sel" : ""}`}
              onClick={() => {
                setUserId(userId === u.id ? "" : u.id);
                setEmail("");
                setName("");
              }}
            >
              <AdAvatar name={u.display_name || u.email} />
              <span className="bd">
                <span className="t">{u.display_name}</span>
                <span className="s">{u.email}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="ad-eyebrow" style={{ marginBottom: 8 }}>
        Or an external address
      </div>
      <div className="ad-grid">
        <Field
          label="Email"
          span
          note={
            client.allowed_email_domains.length > 0
              ? `Must be on ${client.allowed_email_domains.join(" or ")} — the allowed domains on Sign-in & SSO.`
              : "This customer has no allowed email domains set, so every address is refused. Add one on Sign-in & SSO first."
          }
        >
          <input
            type="email"
            value={email}
            disabled={usingUser}
            placeholder={`name@${client.allowed_email_domains[0] ?? "customer.com.au"}`}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Display name" span>
          <input
            value={name}
            disabled={usingUser}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      </div>

      {error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}
    </Drawer>
  );
}
